// src/features/files/components/FileFullscreenViewer.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useFileUrl, downloadFileLink } from '../hooks/useFileUrl';
import { getPreviewKind } from '../utils/previewKind';
import { VideoPlayer } from './VideoPlayer';
import { PdfCanvasViewer, hasNativePdfViewer } from './PdfCanvasViewer';
import { useTranslation } from 'react-i18next';


/**
 * FileFullscreenViewer - Component for viewing files in fullscreen mode
 * Supports PDFs, images, videos and other file types with authenticated access
 */
export function FileFullscreenViewer({ fileId, filename, filetype = null, versionNumber = null, onClose }) {
  const { t } = useTranslation(['files', 'common']);
  const theme = useTheme();
  const [showInfo, setShowInfo] = useState(false);

  // Signed link, streamed by the browser (see useFileUrl)
  const { url: imageUrl, loading, error } = useFileUrl(fileId, { version: versionNumber });

  const kind = getPreviewKind(filetype, filename);

  const handleDownload = () => downloadFileLink(fileId, { version: versionNumber, filename });

  // Escape closes the viewer (not a drawer or dialog it was opened from)
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: theme.zIndex.fullscreen,
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          position: 'relative',
          zIndex: theme.zIndex.fullscreen + 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#fff',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mr: 2,
          }}
        >
          {filename || 'Document'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => setShowInfo(!showInfo)}
            sx={{ color: '#fff' }}
            title="Toggle info"
          >
            <InfoIcon />
          </IconButton>
          <IconButton
            onClick={handleDownload}
            sx={{ color: '#fff' }}
            title="Download file"
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{ color: '#fff' }}
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'auto',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress sx={{ color: '#fff' }} />
            <Typography sx={{ color: '#fff' }}>Loading file...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2, maxWidth: 500 }}>
            {error}
          </Alert>
        )}

        {imageUrl && !loading && (
          kind === 'pdf' && !hasNativePdfViewer() ? (
            <PdfCanvasViewer url={imageUrl} title={filename} />
          ) : kind === 'pdf' ? (
            <embed
              src={imageUrl}
              type="application/pdf"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={filename}
            />
          ) : kind === 'video' ? (
            <VideoPlayer src={imageUrl} title={filename} style={{ width: '100%', height: '100%' }} />
          ) : kind === 'image' ? (
            <img
              src={imageUrl}
              alt={filename}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <embed
              src={imageUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={filename}
            />
          )
        )}
      </Box>

      {/* Info Panel */}
      {showInfo && (
        <Box
          sx={{
            position: 'relative',
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            color: '#fff',
            fontSize: '0.875rem',
            maxHeight: '150px',
            overflowY: 'auto',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ color: '#fff', display: 'block', mb: 1 }}>
            <strong>{t('files:file_name')}:</strong> {filename || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff', display: 'block', mb: 1 }}>
            <strong>{t('files:file_id')}:</strong> {fileId}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff', display: 'block' }}>
            <strong>{t('files:file_type')}:</strong> {filename?.split('.').pop()?.toUpperCase() || '—'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
